import React, { useEffect, Fragment, useState } from "react";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Swal from "sweetalert2";
import {
  GetPharmaCompanies,
  DeletePharmaCompany
} from "../../../../api/medicalStore/master/pharmaCompanyAPI";

const ListPharmaCompany = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fetchData = async () => {
    dispatch({ type: "LOADER_START" });
    try {
      const response = await GetPharmaCompanies();

      if (response.success) {
        showToast("success", response.message);
        const formattedData = response.data.map((item, index) => ({
          index: index + 1,
          id: item._id,
          name: item.name || "",
          shortName: item.shortName || "",
          localContact: item.localCompanyDetails?.contactPerson || "",
          localMobile: item.localCompanyDetails?.mobile || "",
          regionalContact: item.regionalCompanyDetails?.contactPerson || "",
          nationalContact: item.nationalCompanyDetails?.contactPerson || "",
          email:
            item.localCompanyDetails?.email ||
            item.regionalCompanyDetails?.email ||
            item.nationalCompanyDetails?.email ||
            ""
        }));

        setData(formattedData);
        setFilteredData(formattedData);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", "Failed to load companies");
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const results = data.filter(item =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
    setFilteredData(results);
  }, [search, data]);

  const handleEditClick = id => {
    navigate(
      `/medical-store/master/pharma-company/edit/${id}`
    );
  };

  const handleDeleteClick = async id => {
    Swal.fire({
      title: "Are you sure?",
      text: "This company will be deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then(async result => {
      if (result.isConfirmed) {
        dispatch({ type: "LOADER_START" });
        try {
          const response = await DeletePharmaCompany(id);
          if (response.success) {
            showToast("success", response.message);
            setData(data.filter(item => item.id !== id));
            setFilteredData(filteredData.filter(item => item.id !== id));
          } else {
            showToast("error", response.message);
          }
        } catch (error) {
          showToast("error", "Delete failed");
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      }
    });
  };

  const columns = [
    { name: "S.No", selector: row => row.index, sortable: true, width: "10%" },
    { name: "Company Name", selector: row => row.name, sortable: true, width: "15%" },
    { name: "Short Name", selector: row => row.shortName, sortable: true, width: "15%" },
    { name: "Local Contact", selector: row => row.localContact, width: "15%" },
    { name: "Local Mobile", selector: row => row.localMobile, width: "15%" },
    { name: "Regional Contact", selector: row => row.regionalContact, width: "15%" },
    { name: "National Contact", selector: row => row.nationalContact, width: "15%" },
    { name: "Email", selector: row => row.email, width: "15%" },
    {
      name: "Actions",
      cell: row => (
        <div>
          <button
            className="btn btn-primary me-2"
            onClick={() => handleEditClick(row.id)}
            id={`edit-${row.id}`}
          >
            <i className="fa fa-pencil-square-o" />
          </button>
          <UncontrolledTooltip target={`edit-${row.id}`}>
            Edit
          </UncontrolledTooltip>

          <button
            className="btn btn-danger"
            onClick={() => handleDeleteClick(row.id)}
            id={`delete-${row.id}`}
          >
            <i className="fa fa-trash-o" />
          </button>
          <UncontrolledTooltip target={`delete-${row.id}`}>
            Delete
          </UncontrolledTooltip>
        </div>
      ),
      ignoreRowClick: true,
      button: true, 
      width: "20%"
    }
  ];

  const headers = [
    { label: "S.No", key: "index" },
    { label: "Company Name", key: "name" },
    { label: "Short Name", key: "shortName" },
    { label: "Local Contact", key: "localContact" },
    { label: "Local Mobile", key: "localMobile" },
    { label: "Regional Contact", key: "regionalContact" },
    { label: "National Contact", key: "nationalContact" },
    { label: "Email", key: "email" }
  ];

  return (
    <Fragment>
      <Breadcrumb parent="Master" title="Pharma Companies" />
      <div className="container-fluid">
        <div className="card">
          <div className="card-header">
            <div className="d-flex justify-content-between">
              <h5>Pharma Company List</h5>
              <Link to={`/medical-store/master/pharma-company/add`}>
                <button className="btn btn-primary" id="create">
                  Create Company
                </button>
              </Link>
            </div>

            <div className="row mt-2">
              <div className="col-sm-6">
                <input
                  className="form-control"
                  placeholder="Search company..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <div className="col-sm-6 text-end">
                <CSVLink
                  data={data}
                  headers={headers}
                  filename="pharma_companies.csv"
                  className="btn btn-primary"
                >
                  Export
                </CSVLink>
              </div>
            </div>
          </div>

          <div className="card-body">
            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ListPharmaCompany;
